import React, { useState, useEffect, useCallback } from 'react';
import { TopNavigation } from '@/components/layout/TopNavigation';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { Trash2, Plus, Eye, EyeOff, Loader2, CheckCircle2, XCircle, AlertCircle, RefreshCw, DollarSign, TrendingUp, Key, Server, Database, Bell, ExternalLink, KeyRound } from 'lucide-react';
import { getAuthHeaders } from '@/services/authConfig';
import { getUsage, formatUsage } from '@/services/openrouterApi';
import { MCPServersTab, StorageTab, NotificationsTab, MCPKeysTab, SecretsTab } from '@/components/settings';

interface ApiKey {
  id: string;
  provider: string;
  name: string | null;
  key_hint: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  last_used_at: string | null;
  created_by_username: string | null;
}

interface EncryptionStatus {
  configured: boolean;
  message: string;
}

interface OpenRouterUsage {
  limit: number | null;
  limit_remaining: number | null;
  limit_reset: string | null;
  usage: number;
  usage_daily: number;
  usage_weekly: number;
  usage_monthly: number;
  is_free_tier: boolean;
}

interface ProviderField {
  name: string;
  label: string;
  type: 'text' | 'password';
  placeholder?: string;
  default?: string;
}

interface Provider {
  value: string;
  label: string;
  description: string;
  credentialType: 'simple' | 'complex';
  fields: ProviderField[];
}

const PROVIDERS: Provider[] = [
  {
    value: 'openrouter',
    label: 'OpenRouter',
    description: 'Access 200+ models with budget controls',
    credentialType: 'simple',
    fields: [{ name: 'api_key', label: 'API Key', type: 'password' }],
  },
  {
    value: 'openai',
    label: 'OpenAI (Direct)',
    description: 'Direct access to GPT-4, GPT-5, and more',
    credentialType: 'simple',
    fields: [{ name: 'api_key', label: 'API Key', type: 'password' }],
  },
  {
    value: 'anthropic',
    label: 'Anthropic (Direct)',
    description: 'Direct access to Claude models',
    credentialType: 'simple',
    fields: [{ name: 'api_key', label: 'API Key', type: 'password' }],
  },
  {
    value: 'google',
    label: 'Google (Direct)',
    description: 'Direct access to Gemini models',
    credentialType: 'simple',
    fields: [{ name: 'api_key', label: 'API Key', type: 'password' }],
  },
  {
    value: 'bedrock',
    label: 'AWS Bedrock',
    description: 'Access Claude, Llama, Titan via AWS',
    credentialType: 'complex',
    fields: [
      { name: 'aws_access_key_id', label: 'AWS Access Key ID', type: 'text' },
      { name: 'aws_secret_access_key', label: 'AWS Secret Access Key', type: 'password' },
      { name: 'aws_region', label: 'AWS Region', type: 'text', default: 'us-east-1', placeholder: 'us-east-1' },
    ],
  },
  {
    value: 'azure',
    label: 'Azure OpenAI',
    description: 'Azure-hosted OpenAI models',
    credentialType: 'complex',
    fields: [
      { name: 'endpoint', label: 'Endpoint URL', type: 'text', placeholder: 'https://your-resource.openai.azure.com/' },
      { name: 'api_key', label: 'API Key', type: 'password' },
      { name: 'api_version', label: 'API Version', type: 'text', default: '2024-02-15-preview', placeholder: '2024-02-15-preview' },
    ],
  },
];

export function SettingsPage() {
  const { toast } = useToast();
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [encryptionStatus, setEncryptionStatus] = useState<EncryptionStatus | null>(null);

  // Add key form
  const [showAddForm, setShowAddForm] = useState(false);
  const [newKey, setNewKey] = useState<Record<string, string>>({ provider: '', name: '' });
  const [showKeyValues, setShowKeyValues] = useState<Record<string, boolean>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Get the selected provider config
  const selectedProvider = PROVIDERS.find(p => p.value === newKey.provider);

  // Initialize default values when provider changes
  const handleProviderChange = (providerValue: string) => {
    const provider = PROVIDERS.find(p => p.value === providerValue);
    const newData: Record<string, string> = { provider: providerValue, name: '' };

    // Set default values for fields
    if (provider) {
      for (const field of provider.fields) {
        if (field.default) {
          newData[field.name] = field.default;
        }
      }
    }

    setNewKey(newData);
    setShowKeyValues({});
  };

  const toggleFieldVisibility = (fieldName: string) => {
    setShowKeyValues(prev => ({ ...prev, [fieldName]: !prev[fieldName] }));
  };

  // Testing state
  const [testingProvider, setTestingProvider] = useState<string | null>(null);
  const [testResults, setTestResults] = useState<Record<string, { valid: boolean | null; message: string }>>({});

  // OpenRouter usage state
  const [usage, setUsage] = useState<OpenRouterUsage | null>(null);
  const [usageLoading, setUsageLoading] = useState(false);
  const [usageError, setUsageError] = useState<string | null>(null);

  const getHeaders = useCallback(() => {
    return {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    };
  }, []);

  const fetchEncryptionStatus = useCallback(async () => {
    try {
      const response = await fetch('/api/settings/api-keys/encryption-status', {
        credentials: 'include',
        headers: getHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setEncryptionStatus(data);
      }
    } catch (error) {
      console.error('Failed to fetch encryption status:', error);
    }
  }, [getHeaders]);

  const fetchKeys = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await fetch('/api/settings/api-keys', {
        credentials: 'include',
        headers: getHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setKeys(data.keys || []);
      } else if (response.status === 400) {
        // No group selected
        setKeys([]);
      }
    } catch (error) {
      console.error('Failed to fetch API keys:', error);
      toast({
        title: 'Error',
        description: 'Failed to load API keys',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [getHeaders, toast]);

  const fetchUsage = useCallback(async () => {
    // Only fetch if OpenRouter key exists
    const hasOpenRouterKey = keys.some(k => k.provider === 'openrouter');
    if (!hasOpenRouterKey) {
      setUsage(null);
      setUsageError(null);
      return;
    }

    setUsageLoading(true);
    setUsageError(null);
    try {
      const data = await getUsage();
      setUsage(data);
    } catch (error) {
      console.error('Failed to fetch usage:', error);
      setUsageError(error instanceof Error ? error.message : 'Failed to fetch usage');
    } finally {
      setUsageLoading(false);
    }
  }, [keys]);

  useEffect(() => {
    fetchEncryptionStatus();
    fetchKeys();
  }, [fetchEncryptionStatus, fetchKeys]);

  // Fetch usage when keys change (and we have an OpenRouter key)
  useEffect(() => {
    if (keys.length > 0) {
      fetchUsage();
    }
  }, [keys, fetchUsage]);

  const handleAddKey = async () => {
    if (!newKey.provider) {
      toast({
        title: 'Validation Error',
        description: 'Please select a provider',
        variant: 'destructive',
      });
      return;
    }

    // Validate required fields based on provider
    if (selectedProvider) {
      for (const field of selectedProvider.fields) {
        const value = newKey[field.name];
        // Check if field is required (no default) and empty
        if (!field.default && (!value || !value.trim())) {
          toast({
            title: 'Validation Error',
            description: `${field.label} is required`,
            variant: 'destructive',
          });
          return;
        }
      }
    }

    setIsSubmitting(true);
    try {
      const response = await fetch('/api/settings/api-keys', {
        method: 'POST',
        credentials: 'include',
        headers: getHeaders(),
        body: JSON.stringify(newKey),
      });

      if (response.ok) {
        const providerLabel = selectedProvider?.label || newKey.provider;
        toast({
          title: 'Success',
          description: `${providerLabel} credentials saved successfully`,
        });
        setShowAddForm(false);
        setNewKey({ provider: '', name: '' });
        setShowKeyValues({});
        fetchKeys();
      } else {
        const error = await response.json();
        toast({
          title: 'Error',
          description: error.detail || 'Failed to save credentials',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Failed to save credentials:', error);
      toast({
        title: 'Error',
        description: 'Failed to save API key',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteKey = async (provider: string) => {
    if (!confirm(`Are you sure you want to delete the ${provider} API key?`)) {
      return;
    }

    try {
      const response = await fetch(`/api/settings/api-keys/${provider}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: getHeaders(),
      });

      if (response.ok) {
        toast({
          title: 'Deleted',
          description: `API key for ${provider} deleted`,
        });
        fetchKeys();
        // Clear test result for this provider
        setTestResults(prev => {
          const next = { ...prev };
          delete next[provider];
          return next;
        });
      } else {
        const error = await response.json();
        toast({
          title: 'Error',
          description: error.detail || 'Failed to delete API key',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Failed to delete API key:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete API key',
        variant: 'destructive',
      });
    }
  };

  const handleTestKey = async (provider: string) => {
    setTestingProvider(provider);
    try {
      const response = await fetch(`/api/settings/api-keys/${provider}/test`, {
        method: 'POST',
        credentials: 'include',
        headers: getHeaders(),
      });

      if (response.ok) {
        const result = await response.json();
        setTestResults(prev => ({
          ...prev,
          [provider]: { valid: result.valid, message: result.message },
        }));

        if (result.valid === true) {
          toast({
            title: 'Valid',
            description: result.message,
          });
        } else if (result.valid === false) {
          toast({
            title: 'Invalid',
            description: result.message,
            variant: 'destructive',
          });
        } else {
          toast({
            title: 'Unknown',
            description: result.message,
          });
        }
      }
    } catch (error) {
      console.error('Failed to test API key:', error);
      toast({
        title: 'Error',
        description: 'Failed to test API key',
        variant: 'destructive',
      });
    } finally {
      setTestingProvider(null);
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleDateString();
  };

  const getTestStatusIcon = (provider: string) => {
    const result = testResults[provider];
    if (!result) return null;
    if (result.valid === true) return <CheckCircle2 className="h-4 w-4 text-green-500" />;
    if (result.valid === false) return <XCircle className="h-4 w-4 text-red-500" />;
    return <AlertCircle className="h-4 w-4 text-yellow-500" />;
  };

  return (
    <div className="min-h-screen bg-background">
      <TopNavigation />
      <div className="container mx-auto py-8 max-w-4xl">
        <h1 className="text-2xl font-bold mb-6">Settings</h1>

        <Tabs defaultValue="api-keys" className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="api-keys" className="gap-2">
              <Key className="h-4 w-4" />
              API Keys
            </TabsTrigger>
            <TabsTrigger value="mcp-servers" className="gap-2">
              <Server className="h-4 w-4" />
              MCP Servers
            </TabsTrigger>
            <TabsTrigger value="storage" className="gap-2">
              <Database className="h-4 w-4" />
              Data Storage
            </TabsTrigger>
            <TabsTrigger value="notifications" className="gap-2">
              <Bell className="h-4 w-4" />
              Notifications
            </TabsTrigger>
            <TabsTrigger value="mcp-keys" className="gap-2">
              <ExternalLink className="h-4 w-4" />
              MCP Access
            </TabsTrigger>
            <TabsTrigger value="secrets" className="gap-2">
              <KeyRound className="h-4 w-4" />
              Secrets
            </TabsTrigger>
          </TabsList>

          <TabsContent value="api-keys">
            {/* Encryption Warning */}
            {encryptionStatus && !encryptionStatus.configured && (
              <Card className="mb-6 border-yellow-500">
                <CardHeader className="py-4">
                  <CardTitle className="text-yellow-600 flex items-center gap-2">
                    <AlertCircle className="h-5 w-5" />
                    Encryption Not Configured
                  </CardTitle>
                  <CardDescription>
                    API key encryption is not configured. Set the <code className="bg-muted px-1 rounded">API_KEY_ENCRYPTION_KEY</code> environment variable to enable secure key storage.
                  </CardDescription>
                </CardHeader>
              </Card>
            )}

            {/* API Keys Card */}
            <Card>
          <CardHeader>
            <CardTitle>API Keys</CardTitle>
            <CardDescription>
              Manage API keys for LLM providers. Keys are encrypted at rest and scoped to your group.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            ) : (
              <>
                {/* Key list */}
                <div className="space-y-4">
                  {keys.length === 0 && !showAddForm && (
                    <p className="text-muted-foreground text-center py-4">
                      No API keys configured. Add one to get started.
                    </p>
                  )}
                  {keys.map((key) => {
                    const providerConfig = PROVIDERS.find(p => p.value === key.provider);
                    const providerLabel = providerConfig?.label || key.provider;
                    return (
                    <div
                      key={key.id}
                      className="flex items-center justify-between p-4 border rounded-lg"
                    >
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{key.name || providerLabel}</span>
                          <Badge variant="outline">{providerLabel}</Badge>
                          {!key.is_active && (
                            <Badge variant="secondary">Inactive</Badge>
                          )}
                          {getTestStatusIcon(key.provider)}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          Key: {key.key_hint} &bull; Last used: {formatDate(key.last_used_at)}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleTestKey(key.provider)}
                          disabled={testingProvider === key.provider}
                        >
                          {testingProvider === key.provider ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            'Test'
                          )}
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleDeleteKey(key.provider)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  );
                  })}
                </div>

                {/* Add key form */}
                {showAddForm ? (
                  <div className="mt-4 p-4 border rounded-lg space-y-4">
                    <div className="space-y-2">
                      <Label>Provider</Label>
                      <Select
                        value={newKey.provider}
                        onValueChange={handleProviderChange}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select provider" />
                        </SelectTrigger>
                        <SelectContent>
                          {PROVIDERS.map((p) => (
                            <SelectItem key={p.value} value={p.value}>
                              <div className="flex flex-col">
                                <span>{p.label}</span>
                                <span className="text-xs text-muted-foreground">{p.description}</span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Dynamic credential fields based on provider */}
                    {selectedProvider && selectedProvider.fields.map((field) => (
                      <div key={field.name} className="space-y-2">
                        <Label>
                          {field.label}
                          {field.default && <span className="text-muted-foreground ml-1">(optional)</span>}
                        </Label>
                        <div className="relative">
                          <Input
                            type={field.type === 'password' && !showKeyValues[field.name] ? 'password' : 'text'}
                            placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`}
                            value={newKey[field.name] || ''}
                            onChange={(e) => setNewKey({ ...newKey, [field.name]: e.target.value })}
                          />
                          {field.type === 'password' && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                              onClick={() => toggleFieldVisibility(field.name)}
                            >
                              {showKeyValues[field.name] ? (
                                <EyeOff className="h-4 w-4" />
                              ) : (
                                <Eye className="h-4 w-4" />
                              )}
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}

                    {selectedProvider && (
                      <div className="space-y-2">
                        <Label>Name (optional)</Label>
                        <Input
                          placeholder="Give these credentials a name"
                          value={newKey.name || ''}
                          onChange={(e) => setNewKey({ ...newKey, name: e.target.value })}
                        />
                      </div>
                    )}

                    <div className="flex justify-end gap-2">
                      <Button
                        variant="outline"
                        onClick={() => {
                          setShowAddForm(false);
                          setNewKey({ provider: '', name: '' });
                          setShowKeyValues({});
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={handleAddKey}
                        disabled={!newKey.provider || isSubmitting}
                      >
                        {isSubmitting ? (
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        ) : null}
                        Save {selectedProvider?.credentialType === 'complex' ? 'Credentials' : 'Key'}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Button className="mt-4" onClick={() => setShowAddForm(true)}>
                    <Plus className="h-4 w-4 mr-2" /> Add API Key
                  </Button>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* OpenRouter Usage */}
        {keys.some(k => k.provider === 'openrouter') && (
          <Card className="mt-6">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5" />
                    OpenRouter Usage
                  </CardTitle>
                  <CardDescription>
                    Current usage and remaining credits for your OpenRouter API key
                  </CardDescription>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={fetchUsage}
                  disabled={usageLoading}
                >
                  {usageLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {usageLoading && !usage ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : usageError ? (
                <div className="text-center py-4 text-muted-foreground">
                  <AlertCircle className="h-8 w-8 mx-auto mb-2 text-yellow-500" />
                  <p>{usageError}</p>
                </div>
              ) : usage ? (
                <div className="space-y-6">
                  {/* Budget Progress */}
                  {usage.limit !== null && usage.limit_remaining !== null && (
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span>Budget</span>
                        <span className="text-muted-foreground">
                          {formatUsage(usage.limit - usage.limit_remaining)} / {formatUsage(usage.limit)}
                        </span>
                      </div>
                      <Progress
                        value={usage.limit > 0 ? ((usage.limit - usage.limit_remaining) / usage.limit) * 100 : 0}
                        className="h-2"
                      />
                      <div className="flex justify-between text-xs text-muted-foreground">
                        <span>{formatUsage(usage.limit_remaining)} remaining</span>
                        {usage.limit_reset && (
                          <span>Resets: {usage.limit_reset}</span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Usage Breakdown */}
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center p-4 bg-muted rounded-lg">
                      <DollarSign className="h-5 w-5 mx-auto mb-1 text-muted-foreground" />
                      <div className="text-lg font-semibold">{formatUsage(usage.usage_daily)}</div>
                      <div className="text-xs text-muted-foreground">Today</div>
                    </div>
                    <div className="text-center p-4 bg-muted rounded-lg">
                      <DollarSign className="h-5 w-5 mx-auto mb-1 text-muted-foreground" />
                      <div className="text-lg font-semibold">{formatUsage(usage.usage_weekly)}</div>
                      <div className="text-xs text-muted-foreground">This Week</div>
                    </div>
                    <div className="text-center p-4 bg-muted rounded-lg">
                      <DollarSign className="h-5 w-5 mx-auto mb-1 text-muted-foreground" />
                      <div className="text-lg font-semibold">{formatUsage(usage.usage_monthly)}</div>
                      <div className="text-xs text-muted-foreground">This Month</div>
                    </div>
                  </div>

                  {/* Additional Info */}
                  <div className="flex items-center justify-between text-sm pt-2 border-t">
                    <span className="text-muted-foreground">All-time usage</span>
                    <span className="font-medium">{formatUsage(usage.usage)}</span>
                  </div>
                  {usage.is_free_tier && (
                    <Badge variant="secondary" className="w-fit">Free Tier</Badge>
                  )}
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-4">
                  No usage data available
                </p>
              )}
            </CardContent>
          </Card>
        )}

            {/* OpenRouter Info */}
            <Card className="mt-6">
              <CardHeader>
                <CardTitle>Using OpenRouter</CardTitle>
                <CardDescription>
                  OpenRouter provides access to 200+ models through a single API with built-in budget controls.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h4 className="font-medium mb-2">Model Format</h4>
                  <p className="text-sm text-muted-foreground">
                    Use <code className="bg-muted px-1 rounded">openrouter:provider/model</code> format in your agent definitions:
                  </p>
                  <pre className="mt-2 p-3 bg-muted rounded text-sm overflow-x-auto">
{`model: openrouter:anthropic/claude-3-opus
model: openrouter:openai/gpt-4-turbo
model: openrouter:meta-llama/llama-3-70b-instruct`}
              </pre>
                </div>
                <div>
                  <h4 className="font-medium mb-2">Budget Management</h4>
                  <p className="text-sm text-muted-foreground">
                    Set spending limits for your API key in the{' '}
                    <a
                      href="https://openrouter.ai/keys"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      OpenRouter dashboard
                    </a>
                    . Each group can have its own API key with separate budget limits.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="mcp-servers">
            <MCPServersTab />
          </TabsContent>

          <TabsContent value="storage">
            <StorageTab />
          </TabsContent>

          <TabsContent value="notifications">
            <NotificationsTab />
          </TabsContent>

          <TabsContent value="mcp-keys">
            <MCPKeysTab />
          </TabsContent>

          <TabsContent value="secrets">
            <SecretsTab />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
